import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

export type JobType = 'sold' | 'spec';

interface JobTypeContextType {
  jobType: JobType;
  setJobType: (type: JobType) => void;
}

const JobTypeContext = createContext<JobTypeContextType | undefined>(undefined);

interface JobTypeProviderProps {
  children: ReactNode;
}

export const JobTypeProvider: React.FC<JobTypeProviderProps> = ({ children }) => {
  const location = useLocation();
  
  const [jobType, setJobTypeState] = useState<JobType>(() => {
    // Initialize from URL first, then localStorage
    const path = window.location.pathname;
    if (path.startsWith('/spec')) {
      localStorage.setItem('pa.selectedPAType', 'spec');
      return 'spec';
    } else if (path.startsWith('/sold')) {
      localStorage.setItem('pa.selectedPAType', 'sold');
      return 'sold';
    }
    
    // Fall back to localStorage
    const stored = localStorage.getItem('pa.selectedPAType');
    return (stored === 'sold' || stored === 'spec') ? stored : 'sold';
  });

  // Sync jobType with URL changes
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/spec') && jobType !== 'spec') {
      setJobTypeState('spec');
      localStorage.setItem('pa.selectedPAType', 'spec');
    } else if (path.startsWith('/sold') && jobType !== 'sold') {
      setJobTypeState('sold');
      localStorage.setItem('pa.selectedPAType', 'sold');
    }
  }, [location.pathname]);

  const setJobType = (type: JobType) => {
    setJobTypeState(type);
    localStorage.setItem('pa.selectedPAType', type);
  };

  return (
    <JobTypeContext.Provider value={{ jobType, setJobType }}>
      {children}
    </JobTypeContext.Provider>
  );
};

export const useJobType = (): JobTypeContextType => {
  const context = useContext(JobTypeContext);
  if (!context) {
    throw new Error('useJobType must be used within JobTypeProvider');
  }
  return context;
};